/**
 * Fullscreen pixel draw overlay for Theme Creator "Add Here" / animations.
 * Tools: pencil, line, bucket, eraser, eyedropper, color, zoom, import, undo/redo.
 * Animated mode: onion-skin previous frame, game opacity slider.
 * Surfaces: canvas (default), sidebar strip, or background full-stage with UI reference toggles.
 */
import { paintPixel, MAX_ANIM_FRAMES, ANIM_FRAME_MS } from "./shop-creator-fx";

const IMPORT_RESOLUTION_CANDIDATES = [8, 16, 24, 32, 48, 64, 96, 128, 160, 192, 224, 256];

type Tool = "pencil" | "line" | "bucket" | "eraser" | "eyedropper";

export type ThemeDrawSurface = "canvas" | "sidebar" | "background";

export type ThemeDrawResult = {
  imageData: string;
  frames?: string[];
  bounds: { x: number; y: number; w: number; h: number };
  /** 0–1 in-game opacity */
  opacity: number;
};

export type ThemeDrawOverlayOpts = {
  mode: "static" | "animated";
  title?: string;
  /** Drawing context / stage shape. */
  surface?: ThemeDrawSurface;
  /** Theme background colour for background stage. */
  backgroundColor?: string;
  /** Seed canvases when editing an existing decoration. */
  initialFrames?: string[];
  initialOpacity?: number;
  onCancel: () => void;
  onConfirm: (result: ThemeDrawResult) => void;
};

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("load_failed"));
    };
    img.src = url;
  });
}

function loadImageFromDataUrl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("load_failed"));
    img.src = src;
  });
}

function validateImportImageFile(file: File): string | null {
  if (!/^image\/(png|jpeg|jpg)$/i.test(file.type) && !/\.(png|jpe?g)$/i.test(file.name)) {
    return "Use a PNG or JPEG image.";
  }
  if (file.size > 8 * 1024 * 1024) return "Image must be under 8 MB.";
  return null;
}

function downscaleImageToGrid(img: HTMLImageElement, w: number, h: number): ImageData {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

function contentBounds(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const data = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > 8) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { x: 0, y: 0, w, h };
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

function artSizeForSurface(surface: ThemeDrawSurface): { w: number; h: number; zoom: number } {
  if (surface === "sidebar") return { w: 48, h: 128, zoom: 4 };
  if (surface === "background") return { w: 160, h: 90, zoom: 4 };
  return { w: 64, h: 64, zoom: 8 };
}

export function openThemeDrawOverlay(opts: ThemeDrawOverlayOpts): { close: () => void } {
  const surface: ThemeDrawSurface = opts.surface || "canvas";
  const size = artSizeForSurface(surface);
  const artW = size.w;
  const artH = size.h;

  const root = document.createElement("div");
  root.className =
    "themeDrawOverlay" +
    (surface === "background" ? " themeDrawOverlay--background" : "") +
    (surface === "sidebar" ? " themeDrawOverlay--sidebar" : "");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");

  const chrome = document.createElement("div");
  chrome.className = "themeDrawChrome";

  const title = document.createElement("div");
  title.className = "themeDrawTitle";
  title.textContent =
    opts.title ||
    (opts.mode === "animated"
      ? surface === "background"
        ? "Add Animation — Background"
        : surface === "sidebar"
          ? "Add Animation — Sidebar"
          : "Add Animation"
      : "Add Here");
  chrome.appendChild(title);

  const tools = document.createElement("div");
  tools.className = "themeDrawTools";

  let tool: Tool = "pencil";
  let paintColor = "#ffffff";
  let brushSize = 1;
  let zoom = size.zoom;
  let drawing = false;
  let lineStart: { x: number; y: number } | null = null;
  let onionSkin = opts.mode === "animated";
  let gameOpacity =
    typeof opts.initialOpacity === "number" && Number.isFinite(opts.initialOpacity)
      ? Math.max(0.05, Math.min(1, opts.initialOpacity))
      : 1;

  const canvases: HTMLCanvasElement[] = [];
  const contexts: CanvasRenderingContext2D[] = [];
  let frameCount = opts.mode === "animated" ? 2 : 1;
  let frameIndex = 0;

  /** Per-frame undo stacks of ImageData */
  const undoStacks: ImageData[][] = [];
  const redoStacks: ImageData[][] = [];

  function makeFrame() {
    const c = document.createElement("canvas");
    c.width = artW;
    c.height = artH;
    const ctx = c.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, artW, artH);
    canvases.push(c);
    contexts.push(ctx);
    undoStacks.push([]);
    redoStacks.push([]);
  }
  makeFrame();
  if (opts.mode === "animated") makeFrame();

  const seedFrames =
    opts.initialFrames?.filter((s) => typeof s === "string" && s.startsWith("data:image/")) || [];
  if (seedFrames.length) {
    while (canvases.length < seedFrames.length && canvases.length < MAX_ANIM_FRAMES) makeFrame();
    frameCount = Math.max(1, Math.min(MAX_ANIM_FRAMES, seedFrames.length));
    void (async () => {
      for (let i = 0; i < frameCount; i++) {
        const src = seedFrames[i]!;
        try {
          const img = await loadImageFromDataUrl(src);
          const ctx = contexts[i]!;
          ctx.clearRect(0, 0, artW, artH);
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(img, 0, 0, artW, artH);
        } catch {
          /* keep empty frame */
        }
      }
      frameIndex = 0;
      redrawView();
    })();
  }

  function activeCtx() {
    return contexts[frameIndex]!;
  }

  function pushUndo() {
    const snap = activeCtx().getImageData(0, 0, artW, artH);
    undoStacks[frameIndex]!.push(snap);
    if (undoStacks[frameIndex]!.length > 60) undoStacks[frameIndex]!.shift();
    redoStacks[frameIndex] = [];
    syncHistoryBtns();
  }

  function undo() {
    const stack = undoStacks[frameIndex]!;
    if (!stack.length) return;
    const cur = activeCtx().getImageData(0, 0, artW, artH);
    redoStacks[frameIndex]!.push(cur);
    const prev = stack.pop()!;
    activeCtx().putImageData(prev, 0, 0);
    redrawView();
    syncHistoryBtns();
  }

  function redo() {
    const stack = redoStacks[frameIndex]!;
    if (!stack.length) return;
    const cur = activeCtx().getImageData(0, 0, artW, artH);
    undoStacks[frameIndex]!.push(cur);
    const next = stack.pop()!;
    activeCtx().putImageData(next, 0, 0);
    redrawView();
    syncHistoryBtns();
  }

  const view = document.createElement("canvas");
  view.className = "themeDrawView";
  view.width = artW * zoom;
  view.height = artH * zoom;

  function redrawView() {
    view.width = artW * zoom;
    view.height = artH * zoom;
    const vctx = view.getContext("2d")!;
    vctx.imageSmoothingEnabled = false;
    vctx.clearRect(0, 0, view.width, view.height);
    const cell = Math.max(4, zoom);
    for (let y = 0; y < view.height; y += cell) {
      for (let x = 0; x < view.width; x += cell) {
        const on = ((x / cell) | 0) % 2 === ((y / cell) | 0) % 2;
        vctx.fillStyle = on ? "#1a1a22" : "#121218";
        vctx.fillRect(x, y, cell, cell);
      }
    }
    if (onionSkin && opts.mode === "animated" && frameIndex > 0) {
      vctx.globalAlpha = 0.28;
      vctx.drawImage(canvases[frameIndex - 1]!, 0, 0, view.width, view.height);
      vctx.globalAlpha = 1;
    }
    vctx.globalAlpha = gameOpacity;
    vctx.drawImage(canvases[frameIndex]!, 0, 0, view.width, view.height);
    vctx.globalAlpha = 1;
  }

  function setTool(t: Tool) {
    tool = t;
    for (const b of tools.querySelectorAll("button[data-tool]")) {
      b.classList.toggle("green", (b as HTMLElement).dataset.tool === t);
    }
  }

  for (const t of ["pencil", "line", "bucket", "eraser", "eyedropper"] as Tool[]) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "btn";
    b.dataset.tool = t;
    b.textContent = t[0]!.toUpperCase() + t.slice(1);
    b.onclick = () => setTool(t);
    tools.appendChild(b);
  }
  setTool("pencil");

  const undoBtn = document.createElement("button");
  undoBtn.type = "button";
  undoBtn.className = "btn";
  undoBtn.textContent = "Undo";
  undoBtn.onclick = () => undo();
  const redoBtn = document.createElement("button");
  redoBtn.type = "button";
  redoBtn.className = "btn";
  redoBtn.textContent = "Redo";
  redoBtn.onclick = () => redo();
  tools.appendChild(undoBtn);
  tools.appendChild(redoBtn);

  function syncHistoryBtns() {
    undoBtn.disabled = !(undoStacks[frameIndex]?.length);
    redoBtn.disabled = !(redoStacks[frameIndex]?.length);
  }
  syncHistoryBtns();

  const colorInput = document.createElement("input");
  colorInput.type = "color";
  colorInput.value = paintColor;
  colorInput.title = "Color";
  colorInput.oninput = () => {
    paintColor = colorInput.value;
  };
  tools.appendChild(colorInput);

  const sizeLabel = document.createElement("label");
  sizeLabel.className = "themeDrawLabel";
  sizeLabel.textContent = "Size";
  const sizeSel = document.createElement("select");
  sizeSel.className = "formInput";
  for (const n of [1, 2, 3, 4]) {
    const o = document.createElement("option");
    o.value = String(n);
    o.textContent = String(n);
    sizeSel.appendChild(o);
  }
  sizeSel.onchange = () => {
    brushSize = Number(sizeSel.value) || 1;
  };
  sizeLabel.appendChild(sizeSel);
  tools.appendChild(sizeLabel);

  const zoomLabel = document.createElement("label");
  zoomLabel.className = "themeDrawLabel";
  zoomLabel.textContent = "Zoom";
  const zoomSel = document.createElement("select");
  zoomSel.className = "formInput";
  for (const n of [2, 3, 4, 6, 8, 10, 12, 16]) {
    const o = document.createElement("option");
    o.value = String(n);
    o.textContent = `${n}×`;
    if (n === zoom) o.selected = true;
    zoomSel.appendChild(o);
  }
  zoomSel.onchange = () => {
    zoom = Number(zoomSel.value) || 8;
    redrawView();
  };
  zoomLabel.appendChild(zoomSel);
  tools.appendChild(zoomLabel);

  const importBtn = document.createElement("button");
  importBtn.type = "button";
  importBtn.className = "btn";
  importBtn.textContent = "Import";
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = "image/png,image/jpeg,.png,.jpg,.jpeg";
  fileInput.className = "hidden";
  importBtn.onclick = () => fileInput.click();
  tools.appendChild(importBtn);
  tools.appendChild(fileInput);

  chrome.appendChild(tools);

  if (opts.mode === "animated") {
    const frameBar = document.createElement("div");
    frameBar.className = "themeDrawFrameBar";
    const prev = document.createElement("button");
    prev.type = "button";
    prev.className = "btn";
    prev.textContent = "◀";
    const next = document.createElement("button");
    next.type = "button";
    next.className = "btn";
    next.textContent = "▶";
    const info = document.createElement("span");
    info.className = "accessHint";
    const addF = document.createElement("button");
    addF.type = "button";
    addF.className = "btn";
    addF.textContent = "+ Frame";
    const syncInfo = () => {
      info.textContent = `Frame ${frameIndex + 1} / ${frameCount} (max ${MAX_ANIM_FRAMES})`;
      syncHistoryBtns();
    };
    prev.onclick = () => {
      frameIndex = (frameIndex - 1 + frameCount) % frameCount;
      syncInfo();
      redrawView();
    };
    next.onclick = () => {
      frameIndex = (frameIndex + 1) % frameCount;
      syncInfo();
      redrawView();
    };
    addF.onclick = () => {
      if (frameCount >= MAX_ANIM_FRAMES) return;
      makeFrame();
      frameCount++;
      frameIndex = frameCount - 1;
      syncInfo();
      redrawView();
    };

    const onionLab = document.createElement("label");
    onionLab.className = "adminThemeCheckLabel";
    const onionCb = document.createElement("input");
    onionCb.type = "checkbox";
    onionCb.checked = onionSkin;
    onionCb.onchange = () => {
      onionSkin = onionCb.checked;
      redrawView();
    };
    onionLab.appendChild(onionCb);
    onionLab.appendChild(document.createTextNode(" Onion skin (prev frame)"));

    const opacLab = document.createElement("label");
    opacLab.className = "themeDrawLabel";
    opacLab.textContent = "Game opacity";
    const opacRange = document.createElement("input");
    opacRange.type = "range";
    opacRange.min = "5";
    opacRange.max = "100";
    opacRange.value = String(Math.round(gameOpacity * 100));
    const opacVal = document.createElement("span");
    opacVal.textContent = `${Math.round(gameOpacity * 100)}%`;
    opacRange.oninput = () => {
      gameOpacity = Number(opacRange.value) / 100;
      opacVal.textContent = `${opacRange.value}%`;
      redrawView();
    };
    opacLab.appendChild(opacRange);
    opacLab.appendChild(opacVal);

    frameBar.appendChild(prev);
    frameBar.appendChild(info);
    frameBar.appendChild(next);
    frameBar.appendChild(addF);
    frameBar.appendChild(onionLab);
    frameBar.appendChild(opacLab);
    syncInfo();
    chrome.appendChild(frameBar);

    let previewTimer = 0;
    let previewI = 0;
    const previewImg = document.createElement("img");
    previewImg.className = "themeDrawAnimPreview";
    previewImg.alt = "Animation preview";
    previewImg.style.opacity = String(gameOpacity);
    chrome.appendChild(previewImg);
    previewTimer = window.setInterval(() => {
      if (frameCount < 1) return;
      previewI = (previewI + 1) % frameCount;
      previewImg.src = canvases[previewI]!.toDataURL("image/png");
      previewImg.style.opacity = String(gameOpacity);
    }, ANIM_FRAME_MS);
    root.dataset.previewTimer = String(previewTimer);
  }

  /* Background reference toggles */
  let refShowButtons = false;
  let refShowTexts = false;
  let refShowSidebar = false;
  if (surface === "background") {
    const refBar = document.createElement("div");
    refBar.className = "themeDrawFrameBar";
    const hint = document.createElement("span");
    hint.className = "accessHint";
    hint.textContent = "Reference overlays:";
    refBar.appendChild(hint);
    const mk = (label: string, get: () => boolean, set: (v: boolean) => void) => {
      const lab = document.createElement("label");
      lab.className = "adminThemeCheckLabel";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = get();
      cb.onchange = () => {
        set(cb.checked);
        syncRefOverlays();
      };
      lab.appendChild(cb);
      lab.appendChild(document.createTextNode(` ${label}`));
      refBar.appendChild(lab);
    };
    mk("Buttons", () => refShowButtons, (v) => {
      refShowButtons = v;
    });
    mk("Texts", () => refShowTexts, (v) => {
      refShowTexts = v;
    });
    mk("Sidebar", () => refShowSidebar, (v) => {
      refShowSidebar = v;
    });
    chrome.appendChild(refBar);
  }

  const stage = document.createElement("div");
  stage.className =
    "themeDrawStage" +
    (surface === "background" ? " themeDrawStage--background" : "") +
    (surface === "sidebar" ? " themeDrawStage--sidebar" : "");
  if (surface === "background") {
    stage.style.background = opts.backgroundColor || "#000000";
  }

  const refLayer = document.createElement("div");
  refLayer.className = "themeDrawRefLayer";
  refLayer.setAttribute("aria-hidden", "true");
  stage.appendChild(refLayer);
  stage.appendChild(view);
  chrome.appendChild(stage);

  function syncRefOverlays() {
    root.classList.toggle("themeDrawOverlay--refButtons", refShowButtons);
    root.classList.toggle("themeDrawOverlay--refTexts", refShowTexts);
    root.classList.toggle("themeDrawOverlay--refSidebar", refShowSidebar);
    refLayer.replaceChildren();
    if (surface !== "background") return;
    if (refShowSidebar) {
      const side = document.createElement("div");
      side.className = "themeDrawRefSidebar";
      refLayer.appendChild(side);
    }
    if (refShowButtons) {
      for (const label of ["PLAY", "Shop", "Settings"]) {
        const b = document.createElement("div");
        b.className = "themeDrawRefBtn";
        b.textContent = label;
        refLayer.appendChild(b);
      }
    }
    if (refShowTexts) {
      const t = document.createElement("div");
      t.className = "themeDrawRefTitle";
      t.textContent = "CURSORGAME";
      refLayer.appendChild(t);
    }
  }
  syncRefOverlays();

  const actions = document.createElement("div");
  actions.className = "themeDrawActions";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn";
  cancelBtn.textContent = "Cancel";
  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "btn green";
  okBtn.textContent = surface === "background" || surface === "sidebar" ? "Save" : "Place";
  actions.appendChild(cancelBtn);
  actions.appendChild(okBtn);
  chrome.appendChild(actions);

  const status = document.createElement("p");
  status.className = "accessHint";
  status.textContent =
    surface === "background"
      ? "Draw the background animation (behind UI). Toggle references as needed. Undo/Redo available."
      : surface === "sidebar"
        ? "Draw for the sidebar strip. Undo/Redo available."
        : "Draw, then Place. Undo/Redo available.";
  chrome.appendChild(status);

  root.appendChild(chrome);
  document.body.appendChild(root);

  function canvasCoords(ev: PointerEvent) {
    const rect = view.getBoundingClientRect();
    const x = Math.floor(((ev.clientX - rect.left) / rect.width) * artW);
    const y = Math.floor(((ev.clientY - rect.top) / rect.height) * artH);
    return {
      x: Math.max(0, Math.min(artW - 1, x)),
      y: Math.max(0, Math.min(artH - 1, y)),
    };
  }

  function stamp(x: number, y: number, color: string | null) {
    const ctx = activeCtx();
    const r = Math.max(0, brushSize - 1);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const px = x + dx;
        const py = y + dy;
        if (px < 0 || py < 0 || px >= artW || py >= artH) continue;
        paintPixel(ctx, px, py, color, { glow: false, canvasW: artW, canvasH: artH });
      }
    }
  }

  function flood(x: number, y: number, fill: string | null) {
    const ctx = activeCtx();
    const img = ctx.getImageData(0, 0, artW, artH);
    const data = img.data;
    const idx = (x + y * artW) * 4;
    const tr = data[idx]!;
    const tg = data[idx + 1]!;
    const tb = data[idx + 2]!;
    const ta = data[idx + 3]!;
    const stack: Array<[number, number]> = [[x, y]];
    const seen = new Uint8Array(artW * artH);
    const matches = (i: number) =>
      data[i] === tr && data[i + 1] === tg && data[i + 2] === tb && data[i + 3] === ta;
    while (stack.length) {
      const [cx, cy] = stack.pop()!;
      const p = cy * artW + cx;
      if (seen[p]) continue;
      seen[p] = 1;
      const i = p * 4;
      if (!matches(i)) continue;
      if (fill) {
        const n = parseInt(fill.slice(1), 16);
        data[i] = (n >> 16) & 255;
        data[i + 1] = (n >> 8) & 255;
        data[i + 2] = n & 255;
        data[i + 3] = 255;
      } else {
        data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0;
      }
      if (cx > 0) stack.push([cx - 1, cy]);
      if (cx < artW - 1) stack.push([cx + 1, cy]);
      if (cy > 0) stack.push([cx, cy - 1]);
      if (cy < artH - 1) stack.push([cx, cy + 1]);
    }
    ctx.putImageData(img, 0, 0);
  }

  function drawLine(x0: number, y0: number, x1: number, y1: number, color: string | null) {
    let dx = Math.abs(x1 - x0);
    let dy = -Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    let x = x0;
    let y = y0;
    for (;;) {
      stamp(x, y, color);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y += sy;
      }
    }
  }

  view.onpointerdown = (ev) => {
    ev.preventDefault();
    view.setPointerCapture(ev.pointerId);
    const { x, y } = canvasCoords(ev);
    if (tool === "eyedropper") {
      const d = activeCtx().getImageData(x, y, 1, 1).data;
      if (d[3]! > 0) {
        paintColor = `#${[d[0]!, d[1]!, d[2]!].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
        colorInput.value = paintColor;
      }
      return;
    }
    pushUndo();
    if (tool === "bucket") {
      flood(x, y, paintColor);
      redrawView();
      return;
    }
    drawing = true;
    const color = tool === "eraser" ? null : paintColor;
    if (tool === "line") {
      lineStart = { x, y };
      stamp(x, y, color);
    } else {
      stamp(x, y, color);
    }
    redrawView();
  };

  view.onpointermove = (ev) => {
    if (!drawing) return;
    const { x, y } = canvasCoords(ev);
    const color = tool === "eraser" ? null : paintColor;
    if (tool === "line" && lineStart) {
      stamp(x, y, color);
    } else if (tool === "pencil" || tool === "eraser") {
      stamp(x, y, color);
    }
    redrawView();
  };

  view.onpointerup = (ev) => {
    if (!drawing) return;
    drawing = false;
    if (tool === "line" && lineStart) {
      const { x, y } = canvasCoords(ev);
      drawLine(lineStart.x, lineStart.y, x, y, paintColor);
      lineStart = null;
      redrawView();
    }
  };

  function onKey(ev: KeyboardEvent) {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "z") {
      ev.preventDefault();
      if (ev.shiftKey) redo();
      else undo();
    } else if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "y") {
      ev.preventDefault();
      redo();
    }
  }
  window.addEventListener("keydown", onKey);

  function openImportPreview(img: HTMLImageElement, fileName: string) {
    const modal = document.createElement("div");
    modal.className = "themeDrawImportModal";
    const box = document.createElement("div");
    box.className = "modeBox themeDrawImportBox";
    const h = document.createElement("h4");
    h.textContent = `Import — ${fileName}`;
    box.appendChild(h);
    const prev = document.createElement("canvas");
    prev.className = "themeDrawImportPrev";
    prev.width = 160;
    prev.height = 90;
    box.appendChild(prev);
    const resLabel = document.createElement("label");
    resLabel.className = "themeDrawLabel";
    resLabel.textContent = "Resolution";
    const resSel = document.createElement("select");
    resSel.className = "formInput";
    const maxDim = Math.max(artW, artH);
    const optsList = IMPORT_RESOLUTION_CANDIDATES.filter((n) => n <= Math.max(img.width, img.height, maxDim));
    for (const n of optsList.length ? optsList : [64]) {
      const o = document.createElement("option");
      o.value = String(n);
      o.textContent = `${n}×${n}`;
      if (n === Math.min(64, maxDim)) o.selected = true;
      resSel.appendChild(o);
    }
    resLabel.appendChild(resSel);
    box.appendChild(resLabel);

    const refreshPrev = () => {
      const sizeN = Number(resSel.value) || 64;
      const pctx = prev.getContext("2d")!;
      pctx.imageSmoothingEnabled = false;
      pctx.clearRect(0, 0, prev.width, prev.height);
      const grid = downscaleImageToGrid(img, sizeN, sizeN);
      const tmp = document.createElement("canvas");
      tmp.width = sizeN;
      tmp.height = sizeN;
      tmp.getContext("2d")!.putImageData(grid, 0, 0);
      pctx.drawImage(tmp, 0, 0, prev.width, prev.height);
    };
    resSel.onchange = refreshPrev;
    refreshPrev();

    const row = document.createElement("div");
    row.className = "themeDrawActions";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "btn";
    cancel.textContent = "Cancel";
    const cont = document.createElement("button");
    cont.type = "button";
    cont.className = "btn green";
    cont.textContent = "Continue";
    cancel.onclick = () => modal.remove();
    cont.onclick = () => {
      const sizeN = Number(resSel.value) || 64;
      const tw = Math.min(sizeN, artW);
      const th = Math.min(sizeN, artH);
      const grid = downscaleImageToGrid(img, tw, th);
      pushUndo();
      activeCtx().clearRect(0, 0, artW, artH);
      const ox = Math.floor((artW - grid.width) / 2);
      const oy = Math.floor((artH - grid.height) / 2);
      activeCtx().putImageData(grid, Math.max(0, ox), Math.max(0, oy));
      redrawView();
      status.textContent = "Imported — Save/Place when ready.";
      modal.remove();
    };
    row.appendChild(cancel);
    row.appendChild(cont);
    box.appendChild(row);
    modal.appendChild(box);
    root.appendChild(modal);
  }

  fileInput.onchange = async () => {
    const file = fileInput.files?.[0];
    fileInput.value = "";
    if (!file) return;
    const err = validateImportImageFile(file);
    if (err) {
      status.textContent = err;
      return;
    }
    try {
      const img = await loadImageFromFile(file);
      openImportPreview(img, file.name);
    } catch {
      status.textContent = "Could not load image.";
    }
  };

  function close() {
    const t = Number(root.dataset.previewTimer || 0);
    if (t) window.clearInterval(t);
    window.removeEventListener("keydown", onKey);
    root.remove();
  }

  cancelBtn.onclick = () => {
    close();
    opts.onCancel();
  };

  okBtn.onclick = () => {
    const bounds = contentBounds(activeCtx(), artW, artH);
    const imageData = canvases[0]!.toDataURL("image/png");
    const frames =
      opts.mode === "animated" ? canvases.map((c) => c.toDataURL("image/png")) : undefined;
    close();
    opts.onConfirm({ imageData, frames, bounds, opacity: gameOpacity });
  };

  redrawView();

  return { close };
}
